import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { ConfigService } from 'softcat:services/config.ts';
import useSWR from 'swr';
import * as z from 'zod';

type ConfigConfigVersionsProperties = {
    disabled?: boolean;
};

export const ConfigConfigVersions: FunctionComponent<ConfigConfigVersionsProperties> = ({ disabled = false }) => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const [submitting, setSubmitting] = useState(false);
    const [onEditError, setOnEditError] = useState('');
    const [formValues, setFormValues] = useState({ config: '' });

    const EditConfigVersionsSchema = z.object({
        config: z.string().refine(
            (value) => {
                try {
                    JSON.parse(value);

                    return true;
                } catch {
                    return false;
                }
            },
            { message: intl.formatMessage({ id: 'FORM_JSON_VALIDATION' }) },
        ),
    });

    const {
        data: configVersionData,
        error: configVersionDataError,
        isLoading: configVersionDataIsLoading,
    } = useSWR({ key: `${activeTerritory}-ConfigServiceFindAllConfigVersions` }, ConfigService.findAllConfigVersions);

    useEffect(() => {
        if (configVersionData) {
            setFormValues({
                config: JSON.stringify(configVersionData, undefined, 4),
            });
        }
    }, [configVersionData]);

    const onSubmit = async (values: z.infer<typeof EditConfigVersionsSchema>) => {
        setSubmitting(true);
        setOnEditError('');

        if (configVersionData) {
            try {
                await ConfigService.updateAllConfigVersions(JSON.parse(values.config));

                setSubmitting(false);
            } catch (error) {
                setSubmitting(false);
                setOnEditError((error as Error).message);
            }
        }
    };

    return (
        <Form<z.infer<typeof EditConfigVersionsSchema>>
            defaultValues={formValues}
            mode={'onChange'}
            onSubmit={onSubmit}
            resolver={zodResolver(EditConfigVersionsSchema)}
        >
            <div className="space-y-3 px-4">
                <Form.Header
                    description={<FormattedMessage id={'CONFIG_VERSIONS_PAGE_DESCRIPTION'} />}
                    title={<FormattedMessage id={'CONFIG_VERSIONS_PAGE_HEADING'} />}
                />

                {configVersionDataError && (
                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'CONFIG_VERSIONS_PAGE_ERROR_LOADING_DATA'} />
                    </p>
                )}

                {configVersionDataIsLoading && <Spinner />}

                {configVersionData && (
                    <>
                        <Form.Textarea disabled={disabled} label={<FormattedMessage id={'CONFIG_VERSIONS_FORM_CONFIG_LABEL'} />} name={'config'} rows={16} />

                        <Form.Footer
                            disabled={disabled}
                            isSubmitting={submitting}
                            submitError={onEditError && <FormattedMessage id={'CONFIG_VERSIONS_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onEditError }} />}
                            submitText={<FormattedMessage id={'BUTTON_UPDATE'} />}
                        />
                    </>
                )}
            </div>
        </Form>
    );
};
